import { AppShell } from '@/components/layout/AppShell'
import { ProfitTakingItem } from '@/components/profitTaking/ProfitTakingItem'
import { useProfitTakingList } from '@/lib/useProfitTaking'
import type { ProfitTakingPosition, ProfitTriggerSeverity } from '@/types/profitTaking'

const SEVERITY_RANK: Record<ProfitTriggerSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
}

function topSeverityRank(p: ProfitTakingPosition): number {
  let best = 99
  for (const t of p.triggers) {
    const r = SEVERITY_RANK[t.severity] ?? 99
    if (r < best) best = r
  }
  return best
}

function sortBySeverityThenGain(items: ProfitTakingPosition[]): ProfitTakingPosition[] {
  return [...items].sort((a, b) => {
    const sevDiff = topSeverityRank(a) - topSeverityRank(b)
    if (sevDiff !== 0) return sevDiff
    const gainA = a.gain_pct === null ? -Infinity : Number(a.gain_pct)
    const gainB = b.gain_pct === null ? -Infinity : Number(b.gain_pct)
    return gainB - gainA
  })
}

export function ProfitTakingPage() {
  const query = useProfitTakingList()
  const items = query.data?.items ?? []
  const itemsWithTriggers = sortBySeverityThenGain(items.filter((p) => p.triggers.length > 0))
  const itemsWithoutTriggers = sortBySeverityThenGain(items.filter((p) => p.triggers.length === 0))

  return (
    <AppShell>
      <article className="fade-in max-w-4xl">
        <header>
          <p className="tracked-caps text-[10px] text-faint">Gewinnmitnahme</p>
          <h2 className="mt-2 text-2xl text-ink">Profit-Taking</h2>
          <p className="mt-1 font-mono text-xs text-muted">
            {query.data?.total_triggers ?? 0} aktive Trigger · {items.length} Positionen
          </p>
        </header>

        <div className="mt-10">
          {query.isLoading ? (
            <p className="font-mono text-sm text-muted">Lade Auswertung …</p>
          ) : query.isError ? (
            <div role="alert" className="border border-loss/40 bg-loss/5 px-4 py-3">
              <p className="tracked-caps text-[10px] text-loss">Fehler</p>
              <p className="mt-2 font-mono text-sm text-ink">
                Profit-Taking-Daten konnten nicht geladen werden.
              </p>
              <p className="mt-1 font-mono text-xs text-muted">{query.error.message}</p>
            </div>
          ) : items.length === 0 ? (
            <div className="border border-hairline bg-panel px-6 py-12 text-center">
              <p className="tracked-caps text-[10px] text-faint">Leer</p>
              <p className="mt-3 text-sm text-muted">
                Keine Positionen gefunden. Lege erst Positionen unter /portfolio an.
              </p>
            </div>
          ) : (
            <>
              {itemsWithTriggers.length > 0 && (
                <section>
                  <h3 className="tracked-caps mb-3 text-[10px] text-bernstein">Aktive Trigger</h3>
                  <div className="flex flex-col">
                    {itemsWithTriggers.map((p) => (
                      <ProfitTakingItem key={p.position_id} position={p} />
                    ))}
                  </div>
                </section>
              )}

              {itemsWithoutTriggers.length > 0 && (
                <section className="mt-10">
                  <h3 className="tracked-caps mb-3 text-[10px] text-faint">
                    Ohne Trigger ({itemsWithoutTriggers.length})
                  </h3>
                  <div className="flex flex-col">
                    {itemsWithoutTriggers.map((p) => (
                      <ProfitTakingItem key={p.position_id} position={p} compact />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </article>
    </AppShell>
  )
}
